import {
  type FeatureBillingTier,
  isFeatureEnabled,
  type ProjectFeature,
  type SubscriptionFeatureConfig,
} from '@civilgrid/utils/src/common/featureAccess';
import store from '~/store';
import { FEATURE_LEGACY_ORG_FLAG, type OrgFeatureFlag } from './orgFeatureFlags';

// Re-exported so existing importers can keep importing these from '~/authorizations/FeatureFlags'.
export { FEATURE_LEGACY_ORG_FLAG, OrgFeatureFlag } from './orgFeatureFlags';

const FeatureFlags = {
  hasOrgFeatureFlag(f: OrgFeatureFlag) {
    if (!store.currentOrganizationDetail.isLoading) {
      if (store.currentOrganizationDetail.specialFeatures.includes(f)) {
        return true;
      }
    }
    return false;
  },
};

export default FeatureFlags;

/**
 * Whether a project feature is available at a feature gate site (nav + tab router).
 * Enabled via the per-tier matrix OR granted by a legacy org flag (migration-safety for orgs
 * provisioned before feature-provisioning).
 */
export function hasFeatureAccess(
  feature: ProjectFeature,
  tier: FeatureBillingTier,
  subscription: SubscriptionFeatureConfig | null | undefined,
): boolean {
  const legacyFlag = FEATURE_LEGACY_ORG_FLAG[feature];
  if (isFeatureEnabled(feature, tier, subscription)) return true;
  return legacyFlag ? FeatureFlags.hasOrgFeatureFlag(legacyFlag) : false;
}

/**
 * Whether the tab router should redirect a user OFF a feature-gated tab back to the project root.
 *
 * Holds the (destructive) redirect until `orgReady`: the project's own org, which carries the
 * subscription, has loaded.
 *
 * A tab with no `feature` is never redirected here.
 */
export function shouldRedirectOffFeatureTab(params: {
  orgReady: boolean;
  feature: ProjectFeature | undefined;
  tier: FeatureBillingTier;
  subscription: SubscriptionFeatureConfig | null | undefined;
}): boolean {
  const { orgReady, feature, tier, subscription } = params;
  if (!orgReady || !feature) return false;
  return !hasFeatureAccess(feature, tier, subscription);
}
